import type { Chart, LegendItem } from 'chart.js';
import { percentFor } from './pie-percent-labels';

/**
 * Chart colours and option fragments shared by every AVM report section.
 *
 * The tones are the reporting screen's own palette (see `COLORS` / `SEGMENT_COLORS` in
 * src/app/reporting/reporting.component.ts) rather than the material teal/blue-grey set
 * these sections started with, so both analytics screens read as one product.
 */
export const CHART_PALETTE = [
  '#8884d8',
  '#82ca9d',
  '#FFBB28',
  '#0088FE',
  '#00C49F',
  '#FF8042',
  '#AA7799',
  '#554d89',
];

/** Main series (reporting uses it for the current period). */
export const CHART_PRIMARY = CHART_PALETTE[0];
/** Series compared against the main one (reporting uses it for the previous period). */
export const CHART_SECONDARY = CHART_PALETTE[1];
/** Muted counterparts, for a sector/benchmark series drawn next to the mall's own. */
export const CHART_PRIMARY_MUTED = '#554d89';
export const CHART_SECONDARY_MUTED = '#AA7799';

/** Legend block: always on the right, same compact swatch/font as reporting's pie legend. */
export const LEGEND_RIGHT = {
  display: true,
  position: 'right' as const,
  labels: { boxWidth: 12, font: { size: 12 } },
};

/**
 * `maintainAspectRatio: false` is what actually lets the `[style]="{height: 'Nvh'}"` on
 * `<p-chart>` win — with Chart.js's default (true) the canvas sizes itself from its width
 * and the charts rendered far taller than the reporting screen's.
 */
export const CHART_BASE_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
};

/**
 * Legend items for a pie whose names should spell out their own share, e.g. `Giyim (%24)`.
 *
 * Keeps the shape of Chart.js's own pie `generateLabels` — index-based items with the
 * slice's fill — so the default click-to-toggle legend handler keeps working; it only
 * appends the same rounded percent the on-arc labels use (`percentFor`), so legend and
 * arc can never disagree.
 */
export function percentLegendLabels(chart: Chart): LegendItem[] {
  const dataset = chart.data.datasets[0];
  const values = (dataset?.data ?? []) as number[];
  // `backgroundColor` is legally a scalar Color too — indexing a bare string would hand
  // back single characters, so only a real array is treated as per-slice colors.
  const background: unknown = dataset?.backgroundColor;
  const colors = Array.isArray(background) ? (background as string[]) : undefined;

  return (chart.data.labels ?? []).map((label, index) => ({
    text: `${label} (%${percentFor(values, index)})`,
    fillStyle: colors?.[index],
    strokeStyle: colors?.[index],
    lineWidth: 0,
    hidden: !chart.getDataVisibility(index),
    index,
  }));
}

/** `LEGEND_RIGHT`, with each entry carrying its slice's share in parentheses. */
export const LEGEND_RIGHT_WITH_PERCENT = {
  ...LEGEND_RIGHT,
  labels: { ...LEGEND_RIGHT.labels, generateLabels: percentLegendLabels },
};
